import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  RestartAlt,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  lighten,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

import DevicesMap from '../../../map/components/DevicesMap';
import LocationListWidget from './LocationListWidget';

interface MapFullscreenModalProps {
  open: boolean;
  onClose: () => void;
}

const MapFullscreenModal = ({ open, onClose }: MapFullscreenModalProps) => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /* States */

  const [locationListHeight, setLocationListHeight] = useState<
    'collapsed' | 'expanded'
  >('expanded');

  /* API Calls */

  /* Functions & Functional Components */

  const handleResetBounds = () => {
    window.dispatchEvent(new CustomEvent('reset-map-bounds'));
  };

  const toggleLocationList = () => {
    if (!isMobile) return;

    setLocationListHeight(
      locationListHeight === 'collapsed' ? 'expanded' : 'collapsed'
    );
  };

  const handleLocationNavigate = () => {
    if (isMobile && locationListHeight === 'expanded') {
      setLocationListHeight('collapsed');
    }
  };

  const getLocationListHeight = () => {
    if (!isMobile) return 'auto';

    return locationListHeight === 'collapsed' ? '48px' : '70vh';
  };

  const getLocationListWidth = () => {
    if (isMobile) return '100%';
    return '400px';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: theme.tokens.colors.background,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            borderBottom: `1px solid ${theme.tokens.colors.border}`,
            background: lighten(theme.palette.primary.main, 0.95),
          }}
        >
          <Typography
            variant={'h1'}
            fontSize={{
              xs: theme.tokens.typography.fontSize.lg,
              md: theme.tokens.typography.fontSize.xl,
            }}
            fontFamily={theme.tokens.typography.fontFamily.display}
            color={theme.palette.primary.main}
            fontWeight={theme.tokens.typography.fontWeight.semibold}
          >
            Device Map
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title='Reset map view'>
              <IconButton onClick={handleResetBounds} size='small'>
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close fullscreen'>
              <IconButton onClick={onClose} size='small'>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Map Section */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              height: {
                xs:
                  locationListHeight === 'collapsed'
                    ? 'calc(100% - 48px)'
                    : 'calc(100% - 70vh)',
                md: '100%',
              },
              zIndex: 0,
            }}
          >
            <DevicesMap />
          </Box>

          {/* Location List Section */}
          <Box
            sx={{
              width: getLocationListWidth(),
              height: getLocationListHeight(),
              borderTop: {
                xs: `1px solid ${theme.tokens.colors.border}`,
                md: 'none',
              },
              borderLeft: {
                xs: 'none',
                md: `1px solid ${theme.tokens.colors.border}`,
              },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: theme.tokens.colors.card,
              position: { xs: 'absolute', md: 'relative' },
              bottom: { xs: 0, md: 'auto' },
              left: { xs: 0, md: 'auto' },
              right: { xs: 0, md: 'auto' },
              transition: 'height 0.3s ease-in-out',
              zIndex: 1000,
              boxShadow: {
                xs: theme.tokens.shadows.lg,
                md: 'none',
              },
            }}
          >
            {/* Mobile Drag Handle / Toggle */}
            {isMobile && (
              <Box
                onClick={toggleLocationList}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  borderBottom:
                    locationListHeight !== 'collapsed'
                      ? `1px solid ${theme.tokens.colors.border}`
                      : 'none',
                  cursor: 'pointer',
                  backgroundColor: theme.tokens.alpha.black[10],
                  '&:active': {
                    backgroundColor: theme.tokens.alpha.black[30],
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: '40px',
                      height: '4px',
                      borderRadius: '2px',
                      backgroundColor: theme.tokens.colors.mutedForeground,
                      opacity: 0.5,
                    }}
                  />
                  {locationListHeight === 'collapsed' ? (
                    <KeyboardArrowUp fontSize='small' />
                  ) : (
                    <KeyboardArrowDown fontSize='small' />
                  )}
                </Box>
              </Box>
            )}

            {/* Location List Content */}
            {(!isMobile || locationListHeight !== 'collapsed') && (
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <LocationListWidget
                  onItemClick={handleLocationNavigate}
                  cardContainerProps={{ sx: { borderRadius: 0 } }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MapFullscreenModal;
